import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_learn-assessments.scss';
import '../styles/pages/_quiz-completion.scss';

interface QuizQuestion {
  question: string;
  options: string[];
  correctAnswer: number;
  explanation: string;
}

const quizQuestions: QuizQuestion[] = [
  {
    question: "What are the three main steps in effective meal planning?",
    options: [
      "Cook, eat, clean up",
      "Check inventory, plan meals, make list",
      "Buy food, store food, throw away food",
      "Order takeout, heat food, eat food"
    ],
    correctAnswer: 1,
    explanation: "The three main steps are: 1) Check what you already have, 2) Plan balanced meals using those ingredients, and 3) Make a shopping list for missing items. This systematic approach prevents waste and saves money."
  },
  {
    question: "Why is buying seasonal, local produce recommended for meal planning?",
    options: [
      "It's more expensive",
      "Better prices, fresher food, and supports local farmers",
      "It takes longer to prepare",
      "It's harder to find in stores"
    ],
    correctAnswer: 1,
    explanation: "Seasonal, local produce is usually cheaper, fresher, and more nutritious. It also supports local farmers and reduces transportation costs and environmental impact."
  },
  {
    question: "What is the recommended storage time for prepped vegetables in the refrigerator?",
    options: [
      "1 day",
      "3 days",
      "5 days",
      "1 week"
    ],
    correctAnswer: 2,
    explanation: "Prepped vegetables should be stored in airtight containers and used within 5 days. This ensures food safety while allowing you to meal prep efficiently."
  },
  {
    question: "Which shopping strategy can help you get the best value for money?",
    options: [
      "Buying only brand name products",
      "Comparing unit prices on different package sizes",
      "Shopping at only one store",
      "Buying whatever is on sale regardless of need"
    ],
    correctAnswer: 1,
    explanation: "Comparing unit prices (price per kilogram, liter, etc.) helps you understand which package gives the best value. Larger packages aren't always cheaper when you calculate the actual cost per unit."
  },
  {
    question: "What should you do with unused protein portions according to the module?",
    options: [
      "Throw them away",
      "Leave them on the counter",
      "Freeze them for later use",
      "Feed them to pets immediately"
    ],
    correctAnswer: 2,
    explanation: "Unused protein portions should be frozen for later use. This prevents waste and allows you to cook in batches, saving time and money on future meals."
  }
];

const NutritionalModule5QuizPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, isLoading, saveProgress } = useModuleProgress('nutritionalM5');
  const [currentQuestion, setCurrentQuestion] = useState<number>(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [responses, setResponses] = useState<{[key: number]: number}>({});
  const [showCompletion, setShowCompletion] = useState(false);

  const handleAnswerSelect = (answerIndex: number) => {
    if (responses[currentQuestion] === undefined) {
      setSelectedAnswer(answerIndex);
      setShowExplanation(true);
      setResponses(prev => ({...prev, [currentQuestion]: answerIndex}));
    }
  };

  const handleNext = () => {
    if (currentQuestion < quizQuestions.length - 1) {
      const nextQuestion = currentQuestion + 1;
      setCurrentQuestion(nextQuestion);
      setSelectedAnswer(null);
      setShowExplanation(false);
      saveProgress(nextQuestion + 1);
    } else {
      saveProgress(quizQuestions.length, true);
      setShowCompletion(true);
    }
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      const prevQuestion = currentQuestion - 1;
      const previousResponse = responses[prevQuestion];
      setCurrentQuestion(prevQuestion);
      setSelectedAnswer(previousResponse ?? null);
      setShowExplanation(previousResponse !== undefined);
      saveProgress(prevQuestion + 1);
    }
  };

  const handleExit = () => {
    navigate('/panel/learn/nutritional-resources');
  };

  if (showCompletion) {
    return (
      <div className="quiz-completion-section">
        <h2 className="completion-title">
          <span>Congratulations!</span>
        </h2>
        <p className="completion-subtitle">
          You've successfully completed the Module 5 quiz!
        </p>

        <div className="completion-card">
          <div className="achievement-section">
            <h3 className="section-title">
              <span>Key Achievements</span>
            </h3>
            <p className="achievement-intro">You've demonstrated a great understanding of:</p>
            <ul className="achievement-list">
              <li>
                <span className="check"></span>
                <span>Effective meal planning strategies</span>
              </li>
              <li>
                <span className="check"></span>
                <span>Smart shopping and food storage techniques</span>
              </li>
              <li>
                <span className="check"></span>
                <span>Meal preparation and budgeting skills</span>
              </li>
            </ul>
          </div>

          <div className="next-steps-section">
            <h3 className="section-title">
              <span>Next Steps</span>
            </h3>
            <p>Try creating a meal plan for your household!</p>
            <p>Ready to continue your wellness journey?</p>
          </div>

          <button
            className="resource-button"
            onClick={handleExit}
          >
            Return to Nutritional Resources
          </button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading quiz...</p>
      </div>
    );
  }

  const progress = ((currentQuestion + 1) / quizQuestions.length) * 100;
  const currentQuiz = quizQuestions[currentQuestion];

  return (
    <div className="module-form">
      <div className="module-container">
        <div className="module-left nutritional-panel">
          <div className="module-header">
            <h1>Module 5 Quiz</h1>
            <p className="module-subtitle">Special Dietary Considerations</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              Let's review what we've learned about special dietary considerations.
              There's no scoring - this is just to reinforce the main ideas!
            </p>
          </div>
        </div>

        <div className="assessment-right-panel">
          <div className="assessment-content">
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Question {currentQuestion + 1} of {quizQuestions.length}
              </span>
            </div>

            <div className="question-section">
              <h2 className="question-title">
                {currentQuiz.question}
              </h2>
              <p className="question-instruction">
                Please select the answer that best matches your understanding.
              </p>

              <div className="answer-options">
                {currentQuiz.options.map((option, index) => (
                  <label key={index} className="answer-option">
                    <input
                      type="radio"
                      name={`question-${currentQuestion}`}
                      value={index}
                      checked={selectedAnswer === index}
                      onChange={() => handleAnswerSelect(index)}
                      disabled={selectedAnswer !== null}
                    />
                    <span className="option-text">{option}</span>
                  </label>
                ))}
              </div>

              {showExplanation && (
                <div className="explanation">
                  <h3>{selectedAnswer === currentQuiz.correctAnswer ? 'Correct!' : 'Remember:'}</h3>
                  <p>{currentQuiz.explanation}</p>
                </div>
              )}
            </div>
          </div>

          <div className="navigation-buttons">
            <button
              className="btn-secondary"
              onClick={currentQuestion === 0 ? handleExit : handleBack}
            >
              {currentQuestion === 0 ? 'Exit Quiz' : 'Previous Question'}
            </button>

              {currentQuestion < quizQuestions.length - 1 ? (
                <button
                  className="btn-primary"
                  onClick={handleNext}
                  disabled={selectedAnswer === null}
                >
                  Next Question
                </button>
              ) : (
                <button
                  className="btn-primary"
                  onClick={handleNext}
                  disabled={selectedAnswer === null}
                >
                  Complete Quiz
                </button>
              )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NutritionalModule5QuizPage;

